<template>
  <el-cascader
    placeholder="+ Add Process"
    popper-class="category-select"
    clearable
    filterable
    v-model="selectedProcess"
    :options="availableProcesses"
    :props="{value: 'code', label: 'name', children: 'children'}"
    :filter-method="addProcessFilter"
    :disabled="false"
    @change="selectProcess"
    @visible-change="updateOptions"
  >
    <template slot-scope="{ data }">
      <div style="display: flex;align-items: center">
        <tw-entity-icon
          v-if="data.entityId"
          :entityName="data.entityId"
          size="medium"
          color="color"
          style="margin-right: 8px;"
        />
        <span>{{data.name}}</span>
      </div>
    </template>
  </el-cascader>
</template>

<script>
import _ from 'lodash';
import {
  defineComponent,
  ref,
  computed,
  watch,
  reactive,
  onMounted,
} from '@vue/composition-api';

import {
  FROM_TO_TYPE,
  PROCESS_CANCEL_FLG,
  PROCESS_PROGRESS_STATUS,
  DELETE_ABLE_FLG,
  ENTITY_TYPE,
  OTHER_TRADING_FLG,
} from 'lib-tw-common';
import { entityNames, processNames } from '@/dictionaries/map.js';
import { normalize, MAX_PROCESS_NUMBER } from '@/utils/searchUtil';

import TwEntityIcon from '@/components/atoms/TwEntityIcon.vue';

export default defineComponent({
  props: {
    flowInfo: {
      type: Object,
      default: () => {},
    },
    processList: {
      type: Array,
      default: () => [],
    },
  },
  components: {
    TwEntityIcon,
  },
  setup(props, { root, emit }) {

    const localFlowInfo = reactive(props.flowInfo || {});
    const selectedProcess = ref(null);
    const processes = ref([]);

    const availableProcesses = computed(() => {
      if (!localFlowInfo) return [];
      const lastEntityTypeCd = _.get(
        _.last(localFlowInfo.entityStatus),
        'entityTypeCd',
      );
      const flatItems = _.reduce(
        localFlowInfo.entityStatus,
        (res, o) => {
          res = res.concat(
            _.map(o.processStatus, (process, index) => {
              return {
                ..._.omit(o, ['processStatus']),
                ...process,
                entityName: index
                  ? ''
                  : root.$t(
                      `Entity.${_.get(
                        _.find(entityNames, { code: o.entityTypeCd }),
                        'nameEn',
                      )}`,
                    ),
                noLine: index && o.entityTypeCd === lastEntityTypeCd,
              };
            }),
          );
          return res;
        },
        [],
      );

      // 同一プロセスの削除申請がDONEされたらプロセス追加可能
      const items = _.reject(flatItems, o => {
        return (
          o.eventCancel === PROCESS_CANCEL_FLG.ON &&
          [
            PROCESS_PROGRESS_STATUS.AGREED,
            PROCESS_PROGRESS_STATUS.DONE,
          ].includes(o.processProgressStatusCd)
        );
      });
      const entities = _.map(processes.value, o => {
        return {
          ...o,
          children: _.reject(o.children, p => {
            if (!_.find(props.processList, { processType: p.processType })) {
              return true;
            }
            if (
              !['ECREQ', 'ICREQ', 'TPBLI', 'FDCOR', 'FDCOI', 'MIRIP'].includes(
                p.processId,
              )
            ) {
              return _.find(items, { processId: p.processId });
            }
            return (
              _.filter(items, { processType: p.processType }).length >=
              MAX_PROCESS_NUMBER[p.processId]
            );
          }),
        };
      });

      // フローの追加不可エンティティリストに含まれるか、子プロセスがないエンティティは削除。
      // 三国間取引で輸送契約エンティティが登録されている場合も削除
      const ret = _.reject(entities, o => {
        return (
          localFlowInfo.unAddableArea.includes(o.code) ||
          _.isEmpty(o.children) ||
          shouldRejectTransportationAgreement(o)
        );
      });

      return ret;
    });

    /* ========== Methods ========== */

    const addProcessFilter = (node, keyword) => {
      const keywords = node.pathNodes.reduce((ary, pathNode) => {
        if (Array.isArray(pathNode.data.keywords)) {
          return ary.concat(node.pathLabels).concat(pathNode.data.keywords);
        }
        return ary.concat(node.pathLabels);
      }, []);
      return keywords.some(str => {
        return normalize(str).includes(normalize(keyword));
      });
    };

    const selectProcess = process => {
      if (selectedProcess.value) {
        emit('select-process', process);
        root.$nextTick(() => {
          selectedProcess.value = null;
        });
      }
    };

    const updateOptions = data => {
      // console.log('updateOption');
    };

    // 取り引きチャネルが同じフローを輸出、輸入の順で返却します
    const getPairFlows = flow => {
      let flows = [flow];
      const flowType = _.get(flow, 'tradingChannelInfo.flowType');
      if (!flowType) {
        // 三国間取引じゃないときはそのまま返却
        return flows;
      }

      const tradingChannelNo = _.get(
        flow,
        'tradingChannelInfo.tradingChannelNo',
      );
      const channelFlowNo = _.get(flow, 'tradingChannelInfo.channelFlowNo');

      // if (tradingChannelNo) {
      //   flows = _.filter(this.form.items, item => {
      //     return (
      //       _.get(item, 'tradingChannelInfo.tradingChannelNo') ===
      //       tradingChannelNo
      //     );
      //   });
      // } else if (channelFlowNo) {
      //   flows = _.filter(this.form.items, item => {
      //     return (
      //       _.get(item, 'tradingChannelInfo.channelFlowNo') === channelFlowNo
      //     );
      //   });
      // }

      flows = _.sortBy(flows, o => {
        return _.get(o, 'tradingChannelInfo.flowType');
      });

      return flows;
    };

    // 三国間取引で輸送契約エンティティが追加不可かどうかを返却します
    const shouldRejectTransportationAgreement = entity => {
      if (entity.code !== ENTITY_TYPE.TRANSPORTATION_AG) {
        return false;
      }
      const flows = getPairFlows(localFlowInfo);

      if (
        _.some(flows, flow => {
          return _.some(flow.entityStatus, o => {
            return o.entityTypeCd === ENTITY_TYPE.TRANSPORTATION_AG;
          });
        })
      ) {
        return true;
      }
      return false;
    };

    /* ========== Lifecycle ========== */

    onMounted(async () => {
      const categories = _.map(entityNames, o => {
        return {
          ...o,
          name: root.$t(`Entity.${o.nameEn}`),
        };
      });
      processes.value = _.map(categories, o => {
        const children = _.map(
          _.filter(processNames, { parent: o.entityId }),
          p => {
            const processName =
              p.processId === 'TPARB'
                ? root.$t(`Process.TPATPRB`)
                : root.$t(`Process.${p.processId}`);
            return Object.assign(p, { code: p.processId, name: processName });
          },
        );
        return Object.assign({ children: children }, o);
      });
    });

    return {
      addProcessFilter,
      availableProcesses,
      selectedProcess,
      selectProcess,
      updateOptions,
    };
  },
});
</script>
